import React, { useEffect, useState } from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';

import css from './ListingCard.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { createResourceLocatorString } from '../../util/routes';
import { updateFavourites } from '../../containers/SearchPage/SearchPage.duck';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

// Resolve the display label for the listing's `condition` value from the
// listing-fields config (same { option, label } pairs used elsewhere).
// Mirrors resolveEnumLabel in ListingPage.shared.js.
const resolveConditionLabel = (config, publicData) => {
  const condition = publicData?.condition;
  if (!condition) return null;
  const field = (config?.listing?.listingFields || []).find(f => f.key === 'condition');
  const option = (field?.enumOptions || []).find(o => `${o.option}` === `${condition}`);
  return option?.label || null;
};

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    showFavoriteIcons,
    history,
    location,
    routeConfiguration,
  } = props;
  const dispatch = useDispatch()
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const conditionLabel = resolveConditionLabel(config, publicData);
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
      onMouseEnter: () => setActiveListing(currentListing.id),
      onMouseLeave: () => setActiveListing(null),
    }
    : null;

  // For liking functionality
  const { SearchPage: searchPageReducer, user: userReducer } = useSelector(state => state)
  const { currentUser } = userReducer;
  const { updateFavouritesInProgress } = searchPageReducer || {}

  const [liked, setLiked] = useState(false);
  const isOwnListing = author?.id?.uuid === currentUser?.id?.uuid
  const showHeartIcon = showFavoriteIcons && !isOwnListing
  const userFavouriteListings = currentUser && currentUser.attributes.profile.publicData.favoriteListings || [];

  useEffect(() => {
    if (isLiked()) {
      setLiked(true)
    }
  }, [userFavouriteListings])

  const isLiked = () => {
    const likedListings = userFavouriteListings
    return likedListings && likedListings.includes(currentListing.id.uuid);
  };

  const toggleFavourite = (event) => {
    event.preventDefault();

    // If user is not logged in redirect to sign in before they can proceed with favouriting
    if (!currentUser) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };
      history.push(createResourceLocatorString('SignupPage', routeConfiguration, {}, {}), state);
    } else {
      if (updateFavouritesInProgress) return
      setLiked(!liked);

      const likedListings = userFavouriteListings
      let newListings = likedListings ? likedListings : [];
      if (isLiked()) {
        newListings = likedListings.filter(l => l !== currentListing.id.uuid);
      } else {
        newListings.push(currentListing.id.uuid);
      };
      dispatch(updateFavourites(newListings))
    }

  };

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <div className={css.imageWrapper}>
        {conditionLabel ? <div className={css.conditionBadge}>{conditionLabel}</div> : null}
        {showHeartIcon && (
          <div
            className={classNames(css.listingLike, {
              [css.liked]: liked,
              [css.disabled]: updateFavouritesInProgress,
            })}
            onClick={toggleFavourite}
            title={intl.formatMessage({
              id: liked ? 'ListingCard.removeFavourite' : 'ListingCard.addFavourite',
            })}
          >
            {/* The design's outline heart. An inline SVG rather than an image, so
                it takes its colour from CSS: a muted outline that turns coral on
                hover, and fills with that same coral once favourited. */}
            <svg
              className={css.heartIcon}
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z" />
            </svg>
          </div>
        )}
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />
        </AspectRatioWrapper>
      </div>
      <div className={css.info}>
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
        </div>
        {/* Price and seller share one baseline row at the foot of the card. */}
        <div className={css.meta}>
          <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null}
        </div>
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
